import React, { useState, useEffect, useCallback } from 'react';
import { fetchAliases } from './aliasesApi';

const SOURCE_LABELS = {
  cross_listed: 'Cross-listed',
  catalog_formerly: 'Renamed (formerly)',
  credit_only: 'Credit-only',
};

const SOURCE_STYLE = {
  cross_listed: 'bg-blue-100 text-blue-800',
  catalog_formerly: 'bg-amber-100 text-amber-800',
  credit_only: 'bg-gray-100 text-gray-700',
};

const SourceBadge = ({ source }) => (
  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${SOURCE_STYLE[source] || 'bg-gray-100 text-gray-700'}`}>
    {SOURCE_LABELS[source] || source}
  </span>
);

function Aliases() {
  const [searchInput, setSearchInput] = useState('');
  const [committedSearch, setCommittedSearch] = useState('');
  const [source, setSource] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await fetchAliases({ source, search: committedSearch, page, perPage: 50 }));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [source, committedSearch, page]);

  useEffect(() => { load(); }, [load]);

  const submitSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setCommittedSearch(searchInput.trim());
  };

  const onSource = (e) => {
    setPage(1);
    setSource(e.target.value);
  };

  const bySource = (data && data.by_source) || {};

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-bold text-gray-900">Aliases &amp; cross-listings</h2>
        <p className="text-sm text-gray-500">
          Course aliases for the selected institution: cross-listings, catalog renames, and credit-only
          (no-double-count) policies. Read-only — aliases are authored via scripts/migrations.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <form onSubmit={submitSearch} className="flex items-center gap-2">
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search canonical or alias code…"
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md w-64"
          />
          <button type="submit" className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700">
            Search
          </button>
        </form>
        <select value={source} onChange={onSource} className="px-3 py-1.5 text-sm border border-gray-300 rounded-md">
          <option value="">All sources</option>
          {Object.keys(SOURCE_LABELS).map((s) => (
            <option key={s} value={s}>
              {SOURCE_LABELS[s]}{bySource[s] != null ? ` (${bySource[s]})` : ''}
            </option>
          ))}
        </select>
        {data && <span className="text-sm text-gray-500">{data.total} alias{data.total === 1 ? '' : 'es'}</span>}
      </div>

      {error && <div className="text-red-600 text-sm">Error: {error}</div>}

      {loading ? (
        <div className="text-gray-500 text-sm">Loading aliases…</div>
      ) : data && data.aliases.length === 0 ? (
        <div className="bg-white rounded-lg border p-6 text-gray-500 text-sm">No aliases match.</div>
      ) : data ? (
        <>
          <div className="bg-white rounded-lg border overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 text-xs bg-gray-50">
                  <th className="px-3 py-2 font-medium">Canonical</th>
                  <th className="px-3 py-2 font-medium">Alias</th>
                  <th className="px-3 py-2 font-medium">Source</th>
                  <th className="px-3 py-2 font-medium">Title / note</th>
                </tr>
              </thead>
              <tbody>
                {data.aliases.map((a) => (
                  <tr key={`${a.canonical_code}-${a.alias_code}-${a.source}`} className="border-t border-gray-50 align-top">
                    <td className="px-3 py-2 font-mono whitespace-nowrap">{a.canonical_code}</td>
                    <td className="px-3 py-2 font-mono whitespace-nowrap text-gray-600">{a.alias_code}</td>
                    <td className="px-3 py-2"><SourceBadge source={a.source} /></td>
                    <td className="px-3 py-2 text-gray-600">
                      {a.catalog_title || ''}
                      {a.note && <div className="text-xs text-gray-400 mt-0.5">{a.note}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {data.pages > 1 && (
            <div className="flex items-center justify-center gap-3 text-sm">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={data.page <= 1}
                className="px-3 py-1.5 bg-gray-100 rounded-md disabled:opacity-50"
              >
                Previous
              </button>
              <span className="text-gray-500">Page {data.page} of {data.pages}</span>
              <button
                onClick={() => setPage((p) => Math.min(data.pages, p + 1))}
                disabled={data.page >= data.pages}
                className="px-3 py-1.5 bg-gray-100 rounded-md disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}

export default Aliases;
